import "./appClipboardCatcher.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import clipboardCatcherClassName from "../../constants/clipboardCatcherClassName";
import { OUTLINE_VIEW, SPREADSHEET_VIEW, TABLE_VIEW } from "../../constants/identifiers";
import pasteDataTypes from "../../constants/pasteDataTypes";
import PubSubTopics from "../../constants/pubSubTopics";
import { appStateSelectors } from "../../modules/appState";
import { attachmentSelectors } from "../../modules/attachments";
import { dateConstraintSelectors } from "../../modules/dateConstraints";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { typeSelectors } from "../../modules/definition/types";
import { dependencySelectors } from "../../modules/dependencies";
import { itemOperations, itemSelectors } from "../../modules/items";
import { relationshipSelectors } from "../../modules/relationships";
import { sequenceSelectors } from "../../modules/sequence";
import { tagSelectors } from "../../modules/tags";
import { uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getCopyItemsJsonString, getCopyItemsPlainText } from "../../utils/copyPaste";
import isEditable from "../../utils/isEditable";
import selectors from "./selectors";

const tableViewTypes = [SPREADSHEET_VIEW, OUTLINE_VIEW, TABLE_VIEW];

class AppClipboardCatcher extends PureComponent {
  constructor(props) {
    super(props);

    this.onCopy = this.onCopy.bind(this);
    this.onCut = this.onCut.bind(this);
    this.onPaste = this.onPaste.bind(this);

    this._pubSubClipboardFocusToken = null;
    this._clipboardCaptureNode = null;
    this.onClipboardEventPendingMessageReceived = this.onClipboardEventPendingMessageReceived.bind(this);
  }

  componentDidMount() {
    this._pubSubClipboardFocusToken = PubSub.subscribe(
      PubSubTopics.clipboardEventPending,
      this.onClipboardEventPendingMessageReceived
    );
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubClipboardFocusToken);
  }

  canCopy() {
    return !isEditable(document.activeElement) && tableViewTypes.indexOf(this.props.activeViewType) < 0;
  }

  shouldFocusClipboardCatcher() {
    // Not another text field and not in table view
    return this.canCopy();
  }

  focusIsClipboardCatcher() {
    return this._clipboardCaptureNode && document.activeElement === this._clipboardCaptureNode;
  }

  focusClipboardCatcher() {
    if (this._clipboardCaptureNode) {
      this._clipboardCaptureNode.value = " ";
      this._clipboardCaptureNode.focus();
      this._clipboardCaptureNode.setSelectionRange(0, 1);
    }
  }

  removeClipboardFocusIfNecessary() {
    if (this.focusIsClipboardCatcher()) {
      this._clipboardCaptureNode.blur();
    }
  }

  onClipboardEventPendingMessageReceived(_msg) {
    if (this.shouldFocusClipboardCatcher()) {
      this.focusClipboardCatcher();
    }
  }

  onPaste(_e) {
    this.removeClipboardFocusIfNecessary();
  }

  onCopy(e) {
    if (this.focusIsClipboardCatcher()) {
      if (e && e.preventDefault && e.stopPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }

      const {
        itemsById,
        attachmentsById,
        dependenciesById,
        dateConstraintsById,
        relationshipsById,
        propertiesById,
        typesById,
        colorsById,
        tagsById,
        selectedItemIds,
        itemSequence,
        projectId
      } = this.props;

      const aeonItemsData = getCopyItemsJsonString(
        itemsById,
        attachmentsById,
        dependenciesById,
        dateConstraintsById,
        relationshipsById,
        propertiesById,
        typesById,
        colorsById,
        tagsById,
        selectedItemIds,
        itemSequence,
        projectId
      );

      const plainTextItemData = getCopyItemsPlainText(itemsById, selectedItemIds);

      const clipboardData = e.clipboardData || window.clipboardData;
      // Note: adding AEON_ITEMS prefix to indicate copying items
      const prefixedItemData = pasteDataTypes.AEON_ITEMS + aeonItemsData;
      clipboardData.setData(pasteDataTypes.AEON_ITEMS, prefixedItemData);
      clipboardData.setData(pasteDataTypes.PLAINTEXT, plainTextItemData);

      // Finally, remove the focus immediately after this event is called
      this.removeClipboardFocusIfNecessary();
    }
  }

  onCut(e) {
    if (this.focusIsClipboardCatcher()) {
      if (e && e.preventDefault && e.stopPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }
      // Copy
      this.onCopy(e);
      // Delete
      this.props.onDeleteItems(this.props.selectedItemIds);
    }
  }

  render() {
    const { children, appStyle, className } = this.props;
    const classes = ["aeonApp", "desktop", className];
    return (
      <div
        className={classes.join(" ")}
        style={appStyle}
        onCopy={this.onCopy}
        onCut={this.onCut}
        onPaste={this.onPaste}
      >
        {children}
        <input
          type="text"
          defaultValue=" "
          className={`appClipboardCatcher ${clipboardCatcherClassName}`}
          ref={node => (this._clipboardCaptureNode = node)}
        />
      </div>
    );
  }
}

AppClipboardCatcher.propTypes = {
  children: PropTypeShapes.elementOrArray.isRequired,
  appStyle: PropTypes.object.isRequired,
  className: PropTypes.string,
  activeViewType: PropTypes.string,
  itemsById: ImmutablePropTypes.map.isRequired,
  attachmentsById: ImmutablePropTypes.map.isRequired,
  dependenciesById: ImmutablePropTypes.map.isRequired,
  dateConstraintsById: ImmutablePropTypes.map.isRequired,
  relationshipsById: ImmutablePropTypes.map.isRequired,
  propertiesById: ImmutablePropTypes.map.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  colorsById: ImmutablePropTypes.map.isRequired,
  tagsById: ImmutablePropTypes.map.isRequired,
  selectedItemIds: ImmutablePropTypes.list.isRequired,
  itemSequence: ImmutablePropTypes.list.isRequired,
  projectId: PropTypes.string.isRequired,
  onDeleteItems: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    activeViewType: appStateSelectors.getActiveViewType(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    attachmentsById: attachmentSelectors.getAttachmentsById(state.present),
    dependenciesById: dependencySelectors.getDependenciesById(state.present),
    dateConstraintsById: dateConstraintSelectors.getDateConstraintsById(state.present),
    relationshipsById: relationshipSelectors.getRelationshipsById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    tagsById: tagSelectors.getTags(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    itemSequence: sequenceSelectors.getItemSequence(state.present),
    projectId: selectors.getProjectId(state.present)
  };
};

const readonlyActions = {};

const editingActions = {
  onDeleteItems: objIds => {
    return itemOperations.deleteItems(objIds);
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AppClipboardCatcher);
